import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { environment } from '@environment';
import { BehaviorSubject, concat, Observable, of, timer } from 'rxjs';
import { catchError, map, retry, shareReplay, switchMap } from 'rxjs/operators';

export const OVER_SIZED_DELIVERY_LIMIT_KEY = 'overSizedDeliveryLimit';
export const DEFAULT_OVER_SIZED_DELIVERY_LIMIT = 12;

export interface ConfigValueRaw {
    configKey: { code: string };
    value: string;
}

@Injectable({
    providedIn: 'root',
})
export class ConfigValueCacheService {
    private static readonly ENDPOINT = 'config-value/v1/list';
    private static readonly RETRY_DELAY_MS = 30_000;
    private readonly refreshSource$ = new BehaviorSubject<void>(undefined);
    private readonly cache$: Observable<ConfigValueRaw[]>;

    constructor(private http: HttpClient) {
        const url = `${environment.api.url}${environment.api.prefix}/${ConfigValueCacheService.ENDPOINT}`;
        this.cache$ = this.refreshSource$.pipe(
            switchMap(() => this.loadConfigValues(url)),
            shareReplay({ bufferSize: 1, refCount: true })
        );
    }

    public getAll(): Observable<ConfigValueRaw[]> {
        return this.cache$;
    }

    public refresh(): void {
        this.refreshSource$.next();
    }

    public getValue(code: string): Observable<string | undefined> {
        return this.getAll().pipe(map((configs) => configs.find((c) => c.configKey?.code === code)?.value));
    }

    public getNumber(code: string, fallback: number): Observable<number> {
        return this.getValue(code).pipe(
            map((value) => {
                const parsedValue = Number(value);
                return value !== undefined && value.trim() !== '' && Number.isFinite(parsedValue) && parsedValue >= 0 ? parsedValue : fallback;
            }),
            catchError(() => of(fallback))
        );
    }

    private loadConfigValues(url: string): Observable<ConfigValueRaw[]> {
        return this.http.get<ConfigValueRaw[]>(url).pipe(
            retry(2),
            catchError(() => concat(
                of([] as ConfigValueRaw[]),
                timer(ConfigValueCacheService.RETRY_DELAY_MS).pipe(switchMap(() => this.loadConfigValues(url)))
            ))
        );
    }
}
